import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { useToast } from '../../ui'
import { useCommitments, useFlights, useLeaveDays, useLeaveYears, useShowings, useStays, useVisitors } from '../../lib/queries'
import { useAddStay, useAddVisitor, useSetLeaveDay, useUpdateStay, useUpdateVisitor } from '../../lib/mutations'
import { addDays, countryHolidays, fmt, iso, periods } from '../../lib/calendar'
import { holidayMap } from '../../lib/holidays'
import { secondHomeCountry, secondHomeOf, useProfile } from '../../lib/profile'
import { useRegionHolidays } from '../../lib/currentRegion'
import { homeName, useRegionsNow } from '../../lib/regions'
import { defaultLeaveFor, leaveStats } from '../../lib/leaveDays'
import { yearStats } from '../../lib/yearStats'
import { isKeyElsewhere } from '../../lib/typing'
import {
  applyLeave, dayAtX, dayIndex, dragRange, edgeStep, leaveCost, leaveDiff, leaveReading, plansByDay, proposedTrip, segments,
  spanDays, trimDrag, yearEnd, yearLength, yearStart, type DragMode, type Segment,
} from '../../lib/yearSheet'
import type { Stay, Visitor } from '../../lib/types'
import { routeLabel } from '../../lib/flightRoute'
import { DayPane, NewStayPane, StayPane, VisitorPane, type PaneSel } from '../YearPane'
import { TasksCard } from '../../components/TasksCard'
import '../year.css'

const DW = 22
/** The sticky label column; the days start to show at its right edge. */
const LAB = 118
const NONE: never[] = []
const pad = (n: number) => String(n).padStart(2, '0')
const WEEKDAY = 'MTWTFSS'

type Row = 'rachel' | 'home2' | 'travel'
type Preview =
  | { type: 'create'; row: Row; a: string; b: string }
  | { type: 'edit'; what: 'stay' | 'visitor'; id: string; starts_on: string; ends_on: string }
interface Drag {
  mode: 'create' | DragMode
  trk: HTMLElement
  anchor: string
  cur: string
  moved: boolean
  row?: Row
  what?: 'stay' | 'visitor'
  id?: string
  from?: { starts_on: string; ends_on: string }
}

/** A stay's name on a bar and in the ledger: its own label, else what its kind
 *  is called — the profile's second-home label for `home2` (U1.3). */
const placeName = (s: Stay, secondName: string) =>
  s.label || (s.kind === 'home2' ? secondName : s.kind === 'guest_swap' ? 'Home swap' : 'Travel')

/** The ruler's colour token for a kind of stay. */
const KIND_VAR: Record<Stay['kind'], string> = {
  home2: '--yd-home2', travel: '--yd-travel', visit: '--yd-visitor', guest_swap: '--yd-visitor',
}

// ---------------------------------------------------------------------------
// The ruler: month names, weekday letters, date numbers. Memoised, because a
// drag re-renders the screen on every day the pointer crosses and these are
// ~1,100 elements that only change when a leave mark or a stay does.
// ---------------------------------------------------------------------------

interface RulerProps {
  year: number
  today: string
  leave: ReadonlySet<string>
  held: ReadonlySet<string>
  home2: Stay[]
  /** The second home's holidays for the year, by day (`{}` for a person with none). */
  second: Record<string, string>
  overlay: React.ReactNode
  /** The keyboard's day: the one date number in the tab order (a roving
   *  tabindex, so Tab passes the ruler in one stop, not 365). */
  cur: string
}

const Ruler = memo(function Ruler({ year, today, leave, held, home2, second, overlay, cur }: RulerProps) {
  const n = yearLength(year)
  // C2.3: the current region's table, not Berlin's.
  const de = holidayMap(useRegionHolidays(), year)
  const months = []
  for (let m = 1; m <= 12; m++) {
    const d = `${year}-${pad(m)}-01`
    // A jump target (a3-19): the onClick on the sheet reads `data-mo`.
    months.push(
      <button type="button" key={m} className="yd-mo" data-mo={d} style={{ left: dayIndex(year, d) * DW }}
        title={`Go to ${fmt(d, { month: 'long' })}`}>{fmt(d, { month: 'long' })}</button>,
    )
  }
  const letters = []
  const nums = []
  for (let i = 0; i < n; i++) {
    const d = addDays(yearStart(year), i)
    const w = (new Date(`${d}T12:00:00Z`).getUTCDay() + 6) % 7
    const atHome2 = home2.some(s => d >= s.starts_on && d <= s.ends_on)
    const hol = atHome2 ? second[d] : de[d]
    const lv = leave.has(d)
    letters.push(<div key={d} className="yd-wd" style={{ left: i * DW }}>{WEEKDAY[w]}</div>)
    nums.push(
      <div key={d} data-dn={d} role="button" aria-pressed={lv} tabIndex={d === cur ? 0 : -1} aria-describedby="yd-keys"
        aria-label={`${fmt(d, { weekday: 'long', day: 'numeric', month: 'long' })}${hol ? `, ${hol}` : ''}${lv ? ', leave' : ''}`}
        title={`${fmt(d, { weekday: 'long', day: 'numeric', month: 'long' })}${hol ? ` · ${hol}` : ''}${lv ? ' · leave' : ''}`}
        className={`yd-dn${w > 4 ? ' we' : ''}${hol ? ' hol' : ''}${lv ? ' lv' : ''}${lv && held.has(d) ? ' held' : ''}${d === today ? ' now' : ''}`}
        style={{ left: i * DW }}><span>{Number(d.slice(8))}</span></div>,
    )
  }
  return (
    <>
      <div className="yd-lab yd-lab--hd" style={{ height: 26 }}>{year}</div>
      <div className="yd-trk yd-trk--plain" style={{ height: 26 }}>{months}</div>
      <div className="yd-lab yd-lab--hd" style={{ height: 14 }} />
      <div className="yd-trk yd-trk--plain" style={{ height: 14 }}>{letters}</div>
      <div className="yd-lab yd-lab--hd" style={{ height: 22 }} />
      <div className="yd-trk" data-trk="ruler" style={{ height: 22 }}>{overlay}{nums}</div>
    </>
  )
})

export function Year() {
  const toast = useToast()
  const today = iso(new Date())
  const thisYear = Number(today.slice(0, 4))
  const [year, setYear] = useState(thisYear)
  const [sel, setSel] = useState<PaneSel | null>(null)
  // The keyboard's day (a3-12): which date number takes Tab, and the day a
  // focused Plans or Home swap lane opens. Today, or 1 January of another year.
  const [cur, setCur] = useState(today)
  const focusCur = useRef(false)
  const [preview, setPreview] = useState<Preview | null>(null)
  // A drop's dates, held until the cache shows them: between pointer-up and the
  // mutation's optimistic patch there is a tick where the bar would otherwise
  // snap back to where it was.
  const [override, setOverride] = useState<Record<string, { starts_on: string; ends_on: string }>>({})
  const tl = useRef<HTMLDivElement>(null)
  const drag = useRef<Drag | null>(null)
  // A press of Today, or of the year already showing (a3-19): the opening
  // scroll again. A counter, because the year alone does not change.
  const [jump, setJump] = useState(0)
  // Edge scrolling while a drag is held (a3-19): the last pointer x, and the
  // frame loop's handle (0 when not running).
  const lastX = useRef(0)
  const raf = useRef(0)

  const staysQ = useStays()
  const visitorsQ = useVisitors()
  const leaveDaysQ = useLeaveDays()
  const leaveYearsQ = useLeaveYears()
  const commitmentsQ = useCommitments()
  const flightsQ = useFlights()
  // Not read here: the day pane reads it. Mounting it with the screen starts the
  // ~2,400-row fetch before the first click, not on it.
  useShowings()
  const addStay = useAddStay()
  const updateStay = useUpdateStay()
  const addVisitor = useAddVisitor()
  const updateVisitor = useUpdateVisitor()
  const setLeave = useSetLeaveDay()

  const cacheStays = useMemo(() => (staysQ.data ?? NONE).filter(s => s.kind !== 'visit'), [staysQ.data])
  const cacheVisitors = visitorsQ.data ?? NONE
  const commitments = commitmentsQ.data ?? NONE
  const flights = flightsQ.data ?? NONE
  const cacheLeave = useMemo(() => new Set((leaveDaysQ.data ?? NONE).map(l => l.day)), [leaveDaysQ.data])
  const ready = !staysQ.isPending && !leaveDaysQ.isPending && !leaveYearsQ.isPending
  const failed = staysQ.error ?? visitorsQ.error ?? leaveDaysQ.error ?? commitmentsQ.error

  const holidayKey = useRegionHolidays()
  const de = useMemo(() => countryHolidays(holidayKey, year), [holidayKey, year])
  // U1.3: the second home's name and holiday calendar are the profile's.
  const profile = useProfile()
  // C2.1: what a `'Home'` period is called — the home region's name.
  const home = homeName(useRegionsNow(), profile)
  const second = secondHomeOf(profile)
  const secondName = second?.label.trim() || 'Second home'
  const secondCountry = secondHomeCountry(profile)
  const secondHols = useMemo(() => holidayMap(secondCountry, year), [secondCountry, year])

  // What is on screen: the cache, then a drop not yet landed, then a drag in flight.
  const live = <T extends { id: string; starts_on: string; ends_on: string }>(items: T[], what: 's' | 'v'): T[] =>
    items.map(it => {
      if (preview?.type === 'edit' && preview.what === (what === 's' ? 'stay' : 'visitor') && preview.id === it.id) {
        return { ...it, starts_on: preview.starts_on, ends_on: preview.ends_on }
      }
      const o = override[`${what}:${it.id}`]
      return o ? { ...it, ...o } : it
    })
  const stays = live(cacheStays, 's')
  const visitors = live(cacheVisitors, 'v')

  // A travel stay in flight carries its leave marks with it, before any write.
  const leave = useMemo(() => {
    if (preview?.type !== 'edit' || preview.what !== 'stay') return cacheLeave
    const s = cacheStays.find(x => x.id === preview.id)
    if (!s || s.kind !== 'travel') return cacheLeave
    return applyLeave(cacheLeave, leaveDiff(s, preview, cacheLeave, de))
  }, [preview, cacheStays, cacheLeave, de])

  const allowance = (leaveYearsQ.data ?? NONE).find(y => y.year === year)?.allowance ?? 30
  const stats = useMemo(() => leaveStats([...leave], year, allowance, stays), [leave, year, allowance, stays])
  const went = useMemo(() => yearStats(periods(stays, yearStart(year), yearEnd(year)), year).pct, [stays, year])
  const held = useMemo(() => {
    const prov = stays.filter(s => s.provisional)
    return new Set([...leave].filter(d => prov.some(s => d >= s.starts_on && d <= s.ends_on)))
  }, [leave, stays])
  const home2 = useMemo(() => stays.filter(s => s.kind === 'home2'), [stays])

  // ---- selection --------------------------------------------------------
  const selStay = sel?.type === 'stay'
    ? cacheStays.find(s => s.id === sel.id)
      ?? cacheStays.find(s => s.kind === sel.sig.kind && s.starts_on === sel.sig.starts_on && s.ends_on === sel.sig.ends_on)
    : undefined
  const selVisitor = sel?.type === 'visitor'
    ? cacheVisitors.find(v => v.id === sel.id) ?? cacheVisitors.find(v => v.starts_on === sel.sig.starts_on && v.ends_on === sel.sig.ends_on)
    : undefined
  useEffect(() => {
    if (sel?.type === 'stay') {
      if (selStay) {
        if (sel.id !== selStay.id || sel.sig.starts_on !== selStay.starts_on || sel.sig.ends_on !== selStay.ends_on || sel.sig.kind !== selStay.kind) {
          setSel({ type: 'stay', id: selStay.id, sig: { kind: selStay.kind, starts_on: selStay.starts_on, ends_on: selStay.ends_on } })
        }
      } else if (sel.id !== null && !staysQ.isPending) setSel(null)
    } else if (sel?.type === 'visitor') {
      if (selVisitor) {
        if (sel.id !== selVisitor.id || sel.sig.starts_on !== selVisitor.starts_on || sel.sig.ends_on !== selVisitor.ends_on) {
          setSel({ type: 'visitor', id: selVisitor.id, sig: { starts_on: selVisitor.starts_on, ends_on: selVisitor.ends_on } })
        }
      } else if (sel.id !== null && !visitorsQ.isPending) setSel(null)
    }
  }, [sel, selStay, selVisitor, staysQ.isPending, visitorsQ.isPending])

  // A drop's override ends when the cache shows it.
  useEffect(() => {
    setOverride(o => {
      const keys = Object.keys(o)
      if (!keys.length) return o
      const next = { ...o }
      let changed = false
      for (const k of keys) {
        const [what, id] = [k[0], k.slice(2)]
        const cur = what === 's' ? cacheStays.find(s => s.id === id) : cacheVisitors.find(v => v.id === id)
        if (!cur || (cur.starts_on === o[k].starts_on && cur.ends_on === o[k].ends_on)) { delete next[k]; changed = true }
      }
      return changed ? next : o
    })
  }, [cacheStays, cacheVisitors])

  const paneOpen = sel?.type === 'day' || sel?.type === 'new' || !!selStay || !!selVisitor
  const close = () => setSel(null)
  useEffect(() => {
    if (!paneOpen) return
    // Not from a field: Escape there is the field's own (YearPane's
    // `useBlurField` reverts and stops it), and a select or date input that
    // lets it through still means "leave this control", not "close the pane".
    // Nor from the Ask panel or a dialog beside the sheet (a3-09): the pane
    // is not what that Escape was for.
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape' && !isKeyElsewhere(e.target)) setSel(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [paneOpen])

  // ---- scrolling --------------------------------------------------------
  // Opens on today minus 18 days; any other year opens on January. The
  // scroller is always mounted, so this runs on a cold load as well as a warm
  // one; `jump` runs it again on Today or a press of the year on screen.
  useLayoutEffect(() => {
    const el = tl.current
    if (!el) return
    el.scrollLeft = year === thisYear ? Math.max(0, dayIndex(year, addDays(today, -18))) * DW : 0
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, jump])
  const goToday = () => {
    if (year !== thisYear) { setYear(thisYear); setSel(null) }
    setCur(today)
    setJump(j => j + 1)
  }
  // A pane narrows the sheet by 380px, which can push what was just made or
  // picked off the right edge: bring its first day back into view.
  // The room a day can be seen in stops at the pane, wherever it lies: beside
  // the sheet it is outside `tl` already, laid over Ask (year.css, a3-11) it
  // is too, and this stays right if either ever changes.
  const reveal = (d: string, margin = 6) => {
    const el = tl.current
    if (!el || !d.startsWith(`${year}-`)) return
    const pane = el.closest('.yd')?.querySelector('.yd-pane')
    const cover = pane ? Math.max(0, el.getBoundingClientRect().right - pane.getBoundingClientRect().left) : 0
    const x = dayIndex(year, d) * DW
    const room = el.clientWidth - LAB - cover
    if (x < el.scrollLeft) el.scrollLeft = Math.max(0, x - 2 * DW)
    else if (x + DW > el.scrollLeft + room) el.scrollLeft = x - room + margin * DW
  }
  const revealKey = sel?.type === 'day' ? sel.date : sel?.type === 'stay' || sel?.type === 'visitor' ? sel.sig.starts_on
    : sel?.type === 'new' ? sel.draft.starts_on : null
  useLayoutEffect(() => {
    if (revealKey) reveal(revealKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey, year, paneOpen])
  // A move by arrow key takes focus with it, once the ruler has re-rendered
  // the new day into the tab order; the scroll is ours, not the browser's,
  // which would leave the day under the sticky label column.
  useLayoutEffect(() => {
    if (!focusCur.current) return
    focusCur.current = false
    tl.current?.querySelector<HTMLElement>(`[data-dn="${cur}"]`)?.focus({ preventScroll: true })
    reveal(cur, 2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur])
  const scrollToDay = (d: string) => {
    const el = tl.current
    if (!el) return
    const i = Math.max(0, Math.min(yearLength(year) - 1, dayIndex(year, d)))
    el.scrollLeft = Math.max(0, i - 10) * DW
  }

  // ---- the sheet's rows -------------------------------------------------
  const overlay = useMemo(() => {
    const n = yearLength(year)
    const lines = []
    for (let m = 1; m <= 12; m++) lines.push(<div key={`m${m}`} className="yd-ml" style={{ left: dayIndex(year, `${year}-${pad(m)}-01`) * DW }} />)
    const cols = Object.keys(holidayMap(holidayKey, year)).map(d => <div key={`h${d}`} className="yd-hcol" style={{ left: dayIndex(year, d) * DW, width: DW }} />)
    return (
      <>
        {lines}{cols}
        {today.startsWith(`${year}-`) && dayIndex(year, today) < n && <div className="yd-today" style={{ left: dayIndex(year, today) * DW, width: DW }} />}
      </>
    )
  }, [year, today, holidayKey])

  const plans = useMemo(() => plansByDay(commitments, year), [commitments, year])

  const bar = <T extends { id: string }>(seg: Segment<T & { starts_on: string; ends_on: string }>, what: 'stay' | 'visitor', cls: string, name: string, selected: boolean) => (
    <div key={seg.item.id} data-bar={what} data-id={seg.item.id} role="button" tabIndex={0}
      aria-label={`${name}, ${spanDays(seg.item.starts_on, seg.item.ends_on)} days`}
      title={`${name} · ${spanDays(seg.item.starts_on, seg.item.ends_on)} days`}
      className={`yd-bar ${cls}${selected ? ' yd-bar--sel' : ''}${seg.clipL ? ' yd-bar--clipl' : ''}${seg.clipR ? ' yd-bar--clipr' : ''}`}
      style={{ left: seg.col * DW + 1, width: seg.span * DW - 2 }}
      onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBar(what, seg.item.id) } }}>
      {!seg.clipL && <span className="yd-h yd-h--l" data-edge="l" />}
      <span className="yd-bar__t">{name}</span>
      {!seg.clipR && <span className="yd-h yd-h--r" data-edge="r" />}
    </div>
  )
  const openBar = (what: 'stay' | 'visitor', id: string) => {
    if (what === 'stay') {
      const s = stays.find(x => x.id === id)
      if (s) setSel({ type: 'stay', id, sig: { kind: s.kind, starts_on: s.starts_on, ends_on: s.ends_on } })
    } else {
      const v = visitors.find(x => x.id === id)
      if (v) setSel({ type: 'visitor', id, sig: { starts_on: v.starts_on, ends_on: v.ends_on } })
    }
  }
  const isSel = (what: 'stay' | 'visitor', id: string) =>
    (what === 'stay' && selStay?.id === id) || (what === 'visitor' && selVisitor?.id === id)

  // A drag being drawn, or the Add a trip form's draft (a3-21): the same
  // dashed bar, so the form's dates are seen on the sheet before Save.
  const ghost = (row: Row) => {
    const g = preview?.type === 'create' && preview.row === row ? { a: preview.a, b: preview.b }
      : sel?.type === 'new' && sel.draft.kind === row ? { a: sel.draft.starts_on, b: sel.draft.ends_on } : null
    if (!g) return null
    const n = spanDays(g.a, g.b)
    return (
      <div className="yd-bar yd-bar--ghost" style={{ left: dayIndex(year, g.a) * DW + 1, width: n * DW - 2 }}>
        {row === 'travel' ? `${n} days · ${leaveCost(g.a, g.b, de)} leave` : `${n} days`}
      </div>
    )
  }

  // ---- writes -----------------------------------------------------------
  const markLeave = (days: string[], on: boolean) => { for (const day of days) setLeave.mutate({ day, on }) }

  const create = (row: Row, a: string, b: string, label?: string | null) => {
    if (row === 'rachel') {
      addVisitor.mutate({ who: 'Guest', starts_on: a, ends_on: b, at_mine: true })
      setSel({ type: 'visitor', id: null, sig: { starts_on: a, ends_on: b } })
      return
    }
    addStay.mutate({
      kind: row, starts_on: a, ends_on: b, label: label !== undefined ? label : row === 'travel' ? 'New trip' : null,
      provisional: true, country: null,
    })
    if (row === 'travel') markLeave(defaultLeaveFor({ kind: 'travel', starts_on: a, ends_on: b }, de).filter(d => !cacheLeave.has(d)), true)
    setSel({ type: 'stay', id: null, sig: { kind: row, starts_on: a, ends_on: b } })
  }

  // Opens the form; nothing is written until its Save (a3-21).
  const addTrip = () => {
    const t = proposedTrip(year, today)
    setSel({ type: 'new', draft: { kind: 'travel', label: '', ...t } })
    scrollToDay(t.starts_on)
  }
  const saveDraft = () => {
    if (sel?.type !== 'new') return
    const d = sel.draft
    if (!d.starts_on.startsWith(`${year}-`)) setYear(Number(d.starts_on.slice(0, 4)))
    create(d.kind, d.starts_on, d.ends_on, d.label.trim() || null)
  }

  const land = (what: 'stay' | 'visitor', id: string, to: { starts_on: string; ends_on: string }) => {
    const key = `${what === 'stay' ? 's' : 'v'}:${id}`
    setOverride(o => ({ ...o, [key]: to }))
    const clear = () => setOverride(o => { if (!(key in o)) return o; const { [key]: _gone, ...rest } = o; return rest })
    if (what === 'stay') {
      const s = cacheStays.find(x => x.id === id)
      if (!s) return
      // One write for the stay, however many days the pointer crossed.
      updateStay.mutate({ id, patch: { kind: s.kind, label: s.label, provisional: s.provisional, country: s.country, ...to } }, { onSettled: clear })
      if (s.kind === 'travel') {
        const diff = leaveDiff(s, to, cacheLeave, de)
        markLeave(diff.clear, false)
        markLeave(diff.set, true)
      }
    } else {
      const v = cacheVisitors.find(x => x.id === id)
      if (!v) return
      updateVisitor.mutate({ id, patch: { who: v.who, at_mine: v.at_mine, ...to } }, { onSettled: clear })
    }
  }

  // ---- pointer ----------------------------------------------------------
  const dayAt = (d: Drag, clientX: number) => dayAtX(year, clientX, d.trk.getBoundingClientRect().left, DW)

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    const target = e.target as HTMLElement
    const trk = target.closest<HTMLElement>('[data-trk]')
    if (!trk) return
    const row = trk.dataset.trk
    const barEl = target.closest<HTMLElement>('[data-bar]')
    let d: Drag
    if (barEl) {
      const what = barEl.dataset.bar as 'stay' | 'visitor'
      const id = barEl.dataset.id as string
      const ent = (what === 'stay' ? stays : visitors).find(x => x.id === id)
      if (!ent) return
      const edge = target.dataset.edge
      d = { mode: edge === 'l' ? 'resize-l' : edge === 'r' ? 'resize-r' : 'move', trk, anchor: '', cur: '', moved: false, what, id, from: { starts_on: ent.starts_on, ends_on: ent.ends_on } }
    } else if (row === 'rachel' || row === 'home2' || row === 'travel') {
      d = { mode: 'create', trk, anchor: '', cur: '', moved: false, row }
    } else return
    d.anchor = d.cur = dayAt(d, e.clientX)
    drag.current = d
    tl.current?.setPointerCapture(e.pointerId)
    e.preventDefault()
  }

  /** The drag, followed to the day under `clientX`. */
  const follow = (clientX: number) => {
    const d = drag.current
    if (!d) return
    const day = dayAt(d, clientX)
    if (day === d.cur) return
    d.cur = day
    d.moved = true
    if (d.mode === 'create') {
      const r = dragRange(d.anchor, day)
      setPreview({ type: 'create', row: d.row as Row, a: r.starts_on, b: r.ends_on })
    } else {
      const from = d.from as { starts_on: string; ends_on: string }
      const to = trimDrag(d.mode, from.starts_on, from.ends_on, dayIndex(year, day) - dayIndex(year, d.anchor))
      setPreview({ type: 'edit', what: d.what as 'stay' | 'visitor', id: d.id as string, ...to })
    }
  }
  const followRef = useRef(follow)
  followRef.current = follow

  // Held within 40px of an edge, the sheet scrolls 12px a frame under a
  // still pointer, and the drag follows the day now under it (a3-19): a trip
  // could not be dragged past what the sheet showed. Refs only, so the loop
  // started on one render reads the latest.
  const edgeLoop = () => {
    raf.current = 0
    const el = tl.current
    if (!el || !drag.current) return
    const r = el.getBoundingClientRect()
    const step = edgeStep(lastX.current, r.left + LAB, r.right)
    if (!step) return
    const was = el.scrollLeft
    el.scrollLeft = was + step
    if (el.scrollLeft === was) return
    followRef.current(lastX.current)
    raf.current = requestAnimationFrame(edgeLoop)
  }
  const stopEdge = () => { if (raf.current) cancelAnimationFrame(raf.current); raf.current = 0 }
  useEffect(() => stopEdge, [])

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    lastX.current = e.clientX
    follow(e.clientX)
    if (!raf.current) raf.current = requestAnimationFrame(edgeLoop)
  }

  const onPointerUp = () => {
    stopEdge()
    const d = drag.current
    drag.current = null
    if (!d) return
    if (!d.moved) {
      setPreview(null)
      if (d.mode === 'create') setSel({ type: 'day', date: d.anchor })
      else openBar(d.what as 'stay' | 'visitor', d.id as string)
      return
    }
    if (d.mode === 'create') {
      const r = dragRange(d.anchor, d.cur)
      create(d.row as Row, r.starts_on, r.ends_on)
    } else {
      const from = d.from as { starts_on: string; ends_on: string }
      land(d.what as 'stay' | 'visitor', d.id as string, trimDrag(d.mode, from.starts_on, from.ends_on, dayIndex(year, d.cur) - dayIndex(year, d.anchor)))
    }
    setPreview(null)
  }

  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    // A month's name puts its first day at the left edge (a3-19).
    const mo = target.closest<HTMLElement>('[data-mo]')
    if (mo) {
      if (tl.current) tl.current.scrollLeft = dayIndex(year, mo.dataset.mo as string) * DW
      return
    }
    const dn = target.closest<HTMLElement>('[data-dn]')
    if (dn) {
      const day = dn.dataset.dn as string
      setCur(day)
      toggleLeave(day)
      return
    }
    const trk = target.closest<HTMLElement>('[data-trk]')
    if (trk && (trk.dataset.trk === 'plans' || trk.dataset.trk === 'guest_swap')) {
      const day = dayAtX(year, e.clientX, trk.getBoundingClientRect().left, DW)
      setCur(day)
      setSel({ type: 'day', date: day })
    }
  }

  const toggleLeave = (day: string) => {
    const on = !leave.has(day)
    const w = (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7
    // Turning a mark off is always allowed; only marking is refused on a day that cannot be leave.
    if (on && w >= 5) { toast({ text: 'A weekend day is not leave.' }); return }
    if (on && de[day]) { toast({ text: `${de[day]} is a public holiday.` }); return }
    setLeave.mutate({ day, on })
  }

  // The keyboard's way into the sheet (a3-12). On a date number: arrows move
  // the day (Shift for a week), Space marks or clears leave as a click does,
  // Enter opens the day. On a focused Plans or Home swap lane: arrows move the
  // same day, drawn as a column, and Enter or Space opens it.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const t = e.target as HTMLElement
    const onDay = !!t.dataset.dn
    const onLane = t.dataset.trk === 'plans' || t.dataset.trk === 'guest_swap'
    if (!onDay && !onLane) return
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (step) {
      e.preventDefault()
      const i = Math.max(0, Math.min(yearLength(year) - 1, dayIndex(year, cur) + step * (e.shiftKey ? 7 : 1)))
      const next = addDays(yearStart(year), i)
      if (onDay) focusCur.current = true
      else reveal(next, 2)
      setCur(next)
    } else if (e.key === 'Enter' || (e.key === ' ' && onLane)) {
      e.preventDefault()
      setSel({ type: 'day', date: onDay ? (t.dataset.dn as string) : cur })
    } else if (e.key === ' ') {
      e.preventDefault()
      toggleLeave(t.dataset.dn as string)
    }
  }

  const openTrip = (s: Stay) => { openBar('stay', s.id); scrollToDay(s.starts_on < yearStart(year) ? yearStart(year) : s.starts_on) }

  // ---- render -----------------------------------------------------------
  const n = yearLength(year)
  const stayBars = (kind: 'home2' | 'travel') =>
    segments(stays.filter(s => s.kind === kind), year).map(seg =>
      bar(seg, 'stay', `yd-bar--${kind}${seg.item.provisional ? ' yd-bar--opt' : ''}`,
        placeName(seg.item, secondName) + (seg.item.provisional && kind === 'travel' ? ' · option' : ''), isSel('stay', seg.item.id)))
  const visitorBars = segments(visitors, year).map(seg =>
    bar(seg, 'visitor', 'yd-bar--visit', seg.item.who + (seg.item.at_mine ? '' : ' · in town'), isSel('visitor', seg.item.id)))
  const home2Bars = stayBars('home2')
  const travelBars = stayBars('travel')
  // A lane with nothing in this year says how to fill it (a3-21): the
  // crosshair alone did not, and a click there opens a day instead. Sticky,
  // so it stays in view wherever the sheet is scrolled; not drawn under a
  // drag's ghost.
  const hint = (bars: unknown[], row: Row) =>
    bars.length || ghost(row) ? null : <span className="yd-hint" aria-hidden="true">Drag to add</span>
  // The home-swap lane (guests staying while you are away) is drawn once a
  // `guest_swap` stay exists (a3-22, Doug's choice; the kind arrived with
  // U1.3 — it was `kindred` behind a CHECK that refused it); until then it
  // was a labelled strip, empty all year.
  const guestBars = segments(stays.filter(s => s.kind === 'guest_swap'), year).map(seg =>
    bar(seg, 'stay', 'yd-bar--visit', placeName(seg.item, secondName), isSel('stay', seg.item.id)))
  const hasGuestSwap = cacheStays.some(s => s.kind === 'guest_swap')
  // The second-home lane is the profile's. A person with none has no lane —
  // unless a stay of that kind is already on the sheet, which is data a lane
  // must not hide (and what a cold load shows before the profile arrives).
  const hasHome2Lane = !!second || cacheStays.some(s => s.kind === 'home2')

  // A lane is one tab stop; the day it opens is `cur`, named in its label.
  const curName = fmt(cur, { weekday: 'long', day: 'numeric', month: 'long' })
  const laneProps = (name: string) => ({
    role: 'button', tabIndex: 0, 'aria-describedby': 'yd-keys',
    'aria-label': `${name}: open ${curName}`,
    // A click opens the day itself; it should not also leave the lane
    // focused, or the next key draws its keyboard ring under the pane.
    onMouseDown: (e: React.MouseEvent) => { if (e.button === 0) e.preventDefault() },
  })
  const curCol = <div className="yd-cur" aria-hidden="true" style={{ left: dayIndex(year, cur) * DW, width: DW }} />

  const ledger = segments([...stays].sort((a, b) => (a.starts_on < b.starts_on ? -1 : 1)), year)
  const reading = leaveReading(stats.spent, stats.held, allowance)

  return (
    <div className="yd">
      <div className="yd-main">
        <div className="yd-head">
          <div className="yd-seg" role="group" aria-label="Year">
            {[thisYear, thisYear + 1].map(y => (
              <button key={y} type="button" aria-pressed={y === year}
                onClick={() => {
                  // The year already on screen goes back to where it opens (a3-19).
                  if (y === year) { if (y === thisYear) setCur(today); setJump(j => j + 1); return }
                  setYear(y); setSel(null); setCur(y === thisYear ? today : `${y}-01-01`)
                }}>{y}</button>
            ))}
          </div>
          <button type="button" className="yd-btn" onClick={goToday}>Today</button>
          <span className="yd-grow" />
          <button type="button" className="yd-btn" onClick={addTrip}>Add a trip</button>
        </div>
        <div className="yd-leave" aria-busy={!ready}>
          {ready
            ? reading.over > 0
              ? <span className="big yd-over" role="status">{reading.over} <small>{reading.over === 1 ? 'day' : 'days'} over your {allowance}</small></span>
              : <span className="big">{stats.free} <small>of {allowance} leave days free</small></span>
            : <span className="big">– <small>of {allowance} leave days free</small></span>}
          <span className={`yd-track${reading.over > 0 ? ' yd-track--over' : ''}`} role="img"
            aria-label={`${stats.spent} taken or booked, ${stats.held} held by options${reading.over > 0 ? `, ${reading.text}` : ''}`}>
            <i className="spent" style={{ width: `${reading.spent}%` }} />
            <i className="held" style={{ width: `${reading.held}%` }} />
            <i className="over" style={{ width: `${reading.over_pct}%` }} />
            {reading.mark !== null && <b className="mark" style={{ left: `${reading.mark}%` }} />}
          </span>
          <span className="yd-meta"><b className="yd-num">{ready ? stats.spent : '–'}</b> taken or booked · <b className="yd-num">{ready ? stats.held : '–'}</b> held by options</span>
          <span className="yd-went yd-meta">
            <span>{home} <b className="yd-num">{ready ? `${went.berlin}%` : '–'}</b></span>
            {hasHome2Lane && <span>{secondName} <b className="yd-num">{ready ? `${went.home2}%` : '–'}</b></span>}
            <span>Away <b className="yd-num">{ready ? `${went.travel}%` : '–'}</b></span>
          </span>
        </div>
        {failed && <div className="yd-error" role="alert">Could not load the year: {failed.message}</div>}
        <div className="yd-scroll">
          {/* Tasks plan T3 (D10): the Year has no side column, so the card
              sits above the grid, where the rail lands. Absent when nothing
              is open. */}
          <TasksCard desk />
          <div className="yd-tl" ref={tl} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            onPointerCancel={() => { stopEdge(); drag.current = null; setPreview(null) }} onClick={onClick} onKeyDown={onKeyDown}>
            <p id="yd-keys" className="sr">Space marks leave, Enter opens the day, arrow keys move a day and Shift with them a week.</p>
            <div className="yd-grid" style={{ ['--n' as string]: n, ['--wk0' as string]: (new Date(`${yearStart(year)}T12:00:00Z`).getUTCDay() + 6) % 7 }}>
              <Ruler year={year} today={today} leave={leave} held={held} home2={home2} second={secondHols} overlay={overlay} cur={cur} />
              <div className="yd-lab" style={{ height: 30 }}>Rachel</div>
              <div className="yd-trk yd-trk--create" data-trk="rachel" style={{ height: 30 }}>{overlay}{visitorBars}{ghost('rachel')}{hint(visitorBars, 'rachel')}</div>
              {hasHome2Lane && <>
                <div className="yd-lab" style={{ height: 30 }}>{secondName}</div>
                <div className="yd-trk yd-trk--create" data-trk="home2" style={{ height: 30 }}>{overlay}{home2Bars}{ghost('home2')}{hint(home2Bars, 'home2')}</div>
              </>}
              <div className="yd-lab" style={{ height: 30 }}>Travel</div>
              <div className="yd-trk yd-trk--create" data-trk="travel" style={{ height: 30 }}>{overlay}{travelBars}{ghost('travel')}{hint(travelBars, 'travel')}</div>
              {hasGuestSwap && <>
                <div className="yd-lab" style={{ height: 26 }}>Home swap</div>
                <div className="yd-trk yd-trk--click" data-trk="guest_swap" style={{ height: 26 }} {...laneProps('Home swap')}>{overlay}{guestBars}{curCol}</div>
              </>}
              <div className="yd-lab" style={{ height: 124 }}>Plans</div>
              <div className="yd-trk yd-trk--click" data-trk="plans" style={{ height: 124 }} {...laneProps('Plans')}>
                {overlay}{curCol}
                {plans.map(p => (
                  <div key={p.date} className={`yd-vp${p.allGoogle ? ' yd-vp--g' : ''}`} style={{ left: dayIndex(year, p.date) * DW }}
                    title={`${fmt(p.date, { weekday: 'short', day: 'numeric', month: 'short' })} · ${p.text}`}>{p.text}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="yd-trips">
            <table>
              <thead><tr><th>Trip</th><th>Dates</th><th className="r">Days</th><th className="r">Leave</th><th>Flights</th></tr></thead>
              <tbody>
                {ledger.length === 0 && <tr><td colSpan={5} className="yd-meta">Nothing yet.</td></tr>}
                {ledger.map(({ item: s }) => {
                  const lv = [...leave].filter(d => d >= s.starts_on && d <= s.ends_on).length
                  const fl = flights.filter(f => f.on_date >= s.starts_on && f.on_date <= s.ends_on)
                  return (
                    <tr key={s.id} aria-selected={selStay?.id === s.id} tabIndex={0} onClick={() => openTrip(s)}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTrip(s) } }}>
                      <td><i className="yd-swatch" style={{ background: `var(${KIND_VAR[s.kind]})` }} />{placeName(s, secondName)}{s.provisional && <span className="yd-pill">option</span>}</td>
                      <td className="yd-nw">{fmt(s.starts_on, { weekday: 'short', day: 'numeric', month: 'short' })} – {fmt(s.ends_on, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                      <td className="r yd-num">{spanDays(s.starts_on, s.ends_on)}</td>
                      <td className="r yd-num">{lv || '—'}</td>
                      <td className="yd-meta">{fl.map(f => `${routeLabel(f.from_iata, f.to_iata) ?? ''} ${f.code ?? ''}`.trim()).join(' · ')}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {sel?.type === 'day' && <DayPane key={sel.date} date={sel.date} onClose={close} />}
      {sel?.type === 'new' && <NewStayPane draft={sel.draft} onChange={draft => setSel({ type: 'new', draft })} onSave={saveDraft} onClose={close} />}
      {selStay && <StayPane key={`${selStay.id}|${selStay.label}`} stay={selStay as Stay} onClose={close} />}
      {selVisitor && <VisitorPane key={`${selVisitor.id}|${selVisitor.who}`} visitor={selVisitor as Visitor} onClose={close} />}
    </div>
  )
}
